import { Box, Flex, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

import { Button, DeadlineIcon, PhoneIcon } from '#features/ui';
import { Tooltip } from '#features/ui/components/Tooltip';
import { useVoiceActions } from '#features/voice/hooks/useVoiceActions';

/** Один спокойный признак фазы в обеих мини-поверхностях. */
export const RoomCompactStatus = ({
  closed,
  cancelled = false,
  startsIn,
}: {
  closed: boolean;
  cancelled?: boolean;
  startsIn: string | null;
}) => {
  const { t } = useLingui();
  const label = closed
    ? cancelled
      ? t`Session cancelled`
      : t`post:session.room.ended`
    : startsIn
      ? t`post:session.room.startsIn ${startsIn}`
      : t`Session is on`;
  return (
    <Tooltip content={label}>
      <Flex as="span" align="center" gap={1} flexShrink={0} title={label}>
        {closed ? (
          <Text as="span" textStyle="caption" color="fg.muted" whiteSpace="nowrap">
            {label}
          </Text>
        ) : startsIn ? (
          <Flex as="span" align="center" gap={1} color="fg.muted">
            <DeadlineIcon />
            <Text
              as="span"
              textStyle="caption"
              fontVariantNumeric="tabular-nums"
              whiteSpace="nowrap"
            >
              {startsIn}
            </Text>
          </Flex>
        ) : (
          <Box
            as="span"
            boxSize={2}
            borderRadius="full"
            bg="status.live.solid"
            aria-hidden="true"
          />
        )}
      </Flex>
    </Tooltip>
  );
};

/**
 * Свёрнутая комната: правило ширины первой строки и слот действия – одно на пилюлю и на полосу.
 *
 * Обе поверхности показывают одну и ту же строку в одной и той же тесноте, и решать её каждая
 * по-своему они уже пробовали: сперва первая строка вылезала на слот и дробь рисовалась ПОД
 * кнопкой, потом – после починки нахлёста – ужиматься стало единственное, что могло, то есть
 * остаток времени. На стенде это выглядело так: «53 min» показывало «5…», а по-русски «52 мин»
 * не было видно вовсе. Арифметику §5.1 брифа считали по-английски, а русские подписи в этом
 * продукте длиннее почти всегда – значит, правило, живущее в голове, не работает.
 *
 * ПРАВИЛО (порядок уступок в первой строке):
 *
 * 1. Не уступает НИКОГДА – чип состояния (LIVE / «через 09:59» / «Завершена»), остаток времени,
 *    дробь голоса. Это ответы: обрезанный ответ хуже отсутствующего, а «5…» вместо «53 min» и
 *    «LI» вместо «LIVE» – именно обрезанный ответ.
 * 2. Уступают ПЕРВЫМИ – лица: на тот же вопрос («сколько нас слышно») стоящая рядом дробь
 *    отвечает числом, поэтому лицами можно платить, а числом – нет. Обрезаются они с правого
 *    края, то есть первым уходит «+N», за ним второе лицо, а говорящий – последним.
 * 3. Уступает ВТОРОЙ – иконка игры: «какая игра» человек и так знает, он в неё сейчас играет.
 * 4. Слот действия ширины не занимает сверх своей: 40px в пилюле и 44px в полосе во всех
 *    состояниях, кроме входа, где остаётся подпись «Войти» – единственное состояние, где слово
 *    решает, поймут ли действие вовсе. Возврат после обрыва подписи лишился: «Вернуться» – 105px
 *    против 56 у «Join», и эти 49px и съедали остаток. Его слово теперь в `aria-label`, а в
 *    ярусе развёрнутой комнаты по-прежнему стоит кнопка с настоящей подписью.
 *
 * Порядок уступок записан весами `flex-shrink`, а не измерением: измерять в момент вёрстки
 * нечего (текст ещё не разложен), а вес – это ровно «кто отдаёт первым». Флексбокс делит
 * нехватку пропорционально `вес × ширина`, поэтому 100 против 1 означает, что иконка игры
 * начнёт уступать, только когда лица уже кончились.
 */
export const ROW_SHRINK = {
  /** Чип, остаток, счёт голоса – ответы. Не уступают никогда. */
  answer: 0,
  /**
   * Лица – уступают первыми и целиком, до нулевой ширины. Остались у мобильной полосы: там
   * ряд лиц ещё стоит, и платить в узкой строке должен именно он.
   */
  faces: 100,
  /**
   * Иконка игры – тоже не уступает.
   *
   * Она нарисована маской фиксированных 20px, и сжатие коробки её не уменьшает, а ОБРЕЗАЕТ:
   * на стенде 12.09.2026 от лого Battlefield оставалась половина. Пока в строке были лица,
   * уступали они, и до иконки очередь почти не доходила; с их уходом (11.09) первой платить
   * стала именно она, то есть резаться на каждой второй пилюле.
   *
   * Уступает теперь только превью сообщения во второй строке – там обрезка честная, это текст
   * с многоточием.
   */
  gameIcon: 0,
} as const;

type RoomVoiceReturnIconProps = {
  sessionId: string;
  /**
   * Игра и хозяин – подпись плавающего окна голоса: комната голоса данных заявки не видит.
   * Знает их не всякая поверхность, поэтому необязательны.
   */
  gameName?: string | null;
  hostName?: string | null;
  /** Цель нажатия: 44px там, где к ней тянутся пальцем, 40px – там, где целятся мышью. */
  size: '40px' | '44px';
  /** Голос забрало другое устройство: подпись «Вернуть сюда», а не «Вернуться в голос». */
  otherDevice?: boolean;
};

/**
 * «Вернуться в голос» в ширине микрофона – иконочный близнец `VoiceReturnButton` для двух
 * свёрнутых поверхностей.
 *
 * Слово – то же и из того же ключа каталога, только уехало в `aria-label`: читалка и подсказка
 * говорят его целиком, а строка получает назад 65 пикселей, на которых стоят остаток и лица.
 * Смысл ветки не меняется – человека вынесло из голоса не его решением (перезагрузка вкладки),
 * и ему предлагают вернуться, а не «войти» (он уже входил) и не «повторить» (отказа не было).
 *
 * Своя кнопка здесь заведена ровно потому, что подписи в свёрнутом виде быть не должно, а
 * общая кнопка иконочного режима пока не знает. Когда файлы голоса будут свободны, это место
 * обязано стать её режимом (`iconOnly`), а не третьей кнопкой возврата в продукте: одно
 * действие – один компонент.
 */
export const RoomVoiceReturnIcon = ({
  sessionId,
  gameName,
  hostName,
  size,
  otherDevice = false,
}: RoomVoiceReturnIconProps) => {
  const { t } = useLingui();
  const { join, pending } = useVoiceActions();
  const label = otherDevice
    ? t`post:voice.room.otherTabAction`
    : t`post:session.room.returnToVoice`;

  return (
    <Button
      variant="solid"
      colorPalette="purple"
      size="sm"
      w={size}
      minW={size}
      h={size}
      px={0}
      flexShrink={0}
      loading={pending}
      data-testid="voice-return"
      aria-label={label}
      // Подсказка мыши: на десктопе иконка без слова читается только по наведению.
      title={label}
      onClick={() =>
        // Отказ не глотаем молча, а отдаём состоянию: `join` кладёт код в стор, и комната
        // объясняет его сама. Всплывший наружу reject стал бы необработанным промисом.
        void join(sessionId, { gameName, hostName }).catch(() => undefined)
      }
    >
      <PhoneIcon />
    </Button>
  );
};
